// De kluis instellen en openen.
//
// Twee schermen in één: als je nog geen kluis hebt richt je hem in (twee keer hetzelfde
// wachtwoord, en een waarschuwing die je niet kunt missen), en als je er wel een hebt ontgrendel
// je hem. Het wachtwoord gaat nooit naar de server; wat er heen gaat is het zout en een blokje
// waarmee je browser later kan controleren of het klopt.

import { useEffect, useState } from "react";
import { api, type KluisStand } from "../api.ts";
import * as Kluis from "../kluis.ts";
import { Icoon } from "./Icoon.tsx";

export function KluisInstellen({
  stand,
  opStand,
  opMelding
}: {
  stand: KluisStand | null;
  opStand: (stand: KluisStand) => void;
  opMelding: (tekst: string) => void;
}) {
  const [wachtwoord, setWachtwoord] = useState("");
  const [herhaling, setHerhaling] = useState("");
  const [bezig, setBezig] = useState(false);
  const [opnieuw, setOpnieuw] = useState(false);
  const [open, setOpen] = useState(Kluis.isOpen());

  useEffect(() => Kluis.opSlot(setOpen), []);

  // Een nieuw wachtwoord kan alleen als de kluis leeg is. Bestaande notities zouden anders met
  // het oude wachtwoord versleuteld blijven en met het nieuwe nooit meer opengaan.
  const instellen = !stand?.ingesteld || opnieuw;

  async function richtIn() {
    if (wachtwoord.length < 10) return opMelding("Kies een wachtwoord van minstens tien tekens.");
    if (wachtwoord !== herhaling) return opMelding("De twee wachtwoorden zijn niet gelijk.");
    setBezig(true);
    try {
      const zout = Kluis.maakZout();
      const sleutel = await Kluis.afgeleideSleutel(wachtwoord, zout);
      const nieuw = await api.richtKluisIn(zout, Kluis.RONDES, await Kluis.maakControle(sleutel, zout), opnieuw);
      Kluis.ontgrendel(sleutel);
      opStand(nieuw);
      setWachtwoord("");
      setHerhaling("");
      setOpnieuw(false);
      opMelding(opnieuw ? "Het nieuwe kluiswachtwoord staat klaar" : "De kluis staat klaar en is open");
    } catch (fout) {
      opMelding(`Instellen lukte niet: ${(fout as Error).message}`);
    } finally {
      setBezig(false);
    }
  }

  async function ontgrendel() {
    if (!stand?.ingesteld) return;
    setBezig(true);
    try {
      const sleutel = await Kluis.afgeleideSleutel(wachtwoord, stand.zout, stand.rondes);
      if (!(await Kluis.klopt(sleutel, stand.controle))) {
        opMelding("Dat wachtwoord klopt niet.");
        return;
      }
      Kluis.ontgrendel(sleutel);
      setWachtwoord("");
      opMelding("De kluis is open");
    } catch (fout) {
      opMelding(`Openen lukte niet: ${(fout as Error).message}`);
    } finally {
      setBezig(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <h3 style={{ fontSize: 15, margin: 0 }}>Kluis</h3>

      {instellen ? (
        <>
          <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
            {opnieuw
              ? "De kluis is leeg, dus er kan een ander wachtwoord onder. Vanaf nu versleutelt dit apparaat ermee; het oude wachtwoord werkt nergens meer."
              : "Een kluisnotitie wordt op dit apparaat versleuteld en pas hier weer geopend. De NAS bewaart alleen een blok bytes — ook een gestolen schijf of een uitgelekte back-up levert dan niets op."}
          </p>
          <div style={{ padding: 12, border: "1px solid #e3c9ae", background: "#fbf0e6", borderRadius: 10, color: "#8c440f", fontSize: 13 }}>
            <strong>Dit wachtwoord kan niemand voor je terughalen — wij ook niet.</strong> Het staat
            nergens opgeslagen. Ben je het kwijt, dan zijn je kluisnotities weg, ook mét back-up.
            Schrijf het op een plek buiten deze app.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              type="password"
              value={wachtwoord}
              onChange={(e) => setWachtwoord(e.target.value)}
              placeholder="Kluiswachtwoord (minstens 10 tekens)"
              autoComplete="new-password"
              style={{ flexGrow: 1, minWidth: 220, padding: "8px 12px", border: "1px solid var(--lijn)", borderRadius: 8 }}
            />
            <input
              type="password"
              value={herhaling}
              onChange={(e) => setHerhaling(e.target.value)}
              placeholder="Nog een keer"
              autoComplete="new-password"
              style={{ flexGrow: 1, minWidth: 180, padding: "8px 12px", border: "1px solid var(--lijn)", borderRadius: 8 }}
            />
            <button className="knop hoofd" disabled={bezig} onClick={() => void richtIn()}>
              <Icoon naam="sleutel" grootte={15} /> {opnieuw ? "Wachtwoord vervangen" : "Kluis instellen"}
            </button>
            {opnieuw && (
              <button className="knop" onClick={() => setOpnieuw(false)}>
                Laat maar
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
            {stand.notities === 0
              ? "De kluis staat klaar. Open een notitie en klik op het slotje om hem erin te zetten."
              : `${stand.notities} notitie${stand.notities === 1 ? "" : "s"} in de kluis.`}{" "}
            De kluis gaat vanzelf weer op slot na een kwartier zonder gebruik, en altijd als je de
            app sluit. De titel van een kluisnotitie blijft leesbaar — zet daar dus geen wachtwoord in.
          </p>
          {open ? (
            <div>
              <button className="knop" onClick={() => Kluis.vergrendel()}>
                <Icoon naam="sleutel" grootte={15} /> Nu op slot doen
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                type="password"
                value={wachtwoord}
                onChange={(e) => setWachtwoord(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void ontgrendel();
                }}
                placeholder="Kluiswachtwoord"
                autoComplete="current-password"
                style={{ flexGrow: 1, minWidth: 220, padding: "8px 12px", border: "1px solid var(--lijn)", borderRadius: 8 }}
              />
              <button className="knop hoofd" disabled={bezig} onClick={() => void ontgrendel()}>
                {bezig ? "Bezig…" : "Kluis openen"}
              </button>
            </div>
          )}

          <p style={{ margin: 0, fontSize: 12, color: "var(--gedempt)" }}>
            {stand.notities === 0 ? (
              <button className="knop stil" onClick={() => setOpnieuw(true)}>
                Ander kluiswachtwoord kiezen
              </button>
            ) : (
              "Een ander kluiswachtwoord kiezen kan pas als er niets meer in de kluis zit — anders zouden die notities met het oude wachtwoord versleuteld blijven."
            )}
          </p>
        </>
      )}
    </section>
  );
}

/** Het scherm in plaats van de editor zolang een kluisnotitie op slot staat. */
export function OpSlot({ stand, opMelding }: { stand: KluisStand | null; opMelding: (tekst: string) => void }) {
  const [wachtwoord, setWachtwoord] = useState("");
  const [bezig, setBezig] = useState(false);

  async function ontgrendel() {
    if (!stand?.ingesteld) return;
    setBezig(true);
    try {
      const sleutel = await Kluis.afgeleideSleutel(wachtwoord, stand.zout, stand.rondes);
      if (!(await Kluis.klopt(sleutel, stand.controle))) return opMelding("Dat wachtwoord klopt niet.");
      Kluis.ontgrendel(sleutel);
      setWachtwoord("");
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="editorblad">
      <div className="editorbinnen" style={{ alignItems: "center", gap: 14, paddingTop: 60, textAlign: "center" }}>
        <Icoon naam="sleutel" grootte={34} />
        <p style={{ margin: 0, maxWidth: 380, color: "var(--gedempt)", fontSize: 14 }}>
          Deze notitie staat in de kluis. Hij is versleuteld op je eigen apparaat; typ je
          kluiswachtwoord om hem te lezen.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            autoFocus
            type="password"
            value={wachtwoord}
            onChange={(e) => setWachtwoord(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void ontgrendel();
            }}
            placeholder="Kluiswachtwoord"
            autoComplete="current-password"
            style={{ padding: "9px 12px", border: "1px solid var(--lijn)", borderRadius: 9, minWidth: 220 }}
          />
          <button className="knop hoofd" disabled={bezig} onClick={() => void ontgrendel()}>
            {bezig ? "Bezig…" : "Openen"}
          </button>
        </div>
      </div>
    </div>
  );
}
